import { alpha, type Theme } from '@mui/material/styles';
import { type ButtonProps, buttonClasses } from '@mui/material/Button';

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// NEW VARIANT
declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    xl: true;
  }
  interface ButtonPropsVariantOverrides {
    soft: true;
    white: true;
  }
}

// ----------------------------------------------------------------------

export function button(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: ButtonProps) => {
    const inheritColor = ownerState.color === 'inherit';

    const containedVariant = ownerState.variant === 'contained';

    const outlinedVariant = ownerState.variant === 'outlined';

    const textVariant = ownerState.variant === 'text';

    const softVariant = ownerState.variant === 'soft';

    const whiteVariant = ownerState.variant === 'white';

    const smallSize = ownerState.size === 'small';

    const mediumSize = ownerState.size === 'medium';

    const largeSize = ownerState.size === 'large';

    const xlSize = ownerState.size === 'xl';

    const defaultStyle = {
      ...(inheritColor && {
        // CONTAINED
        ...(containedVariant && {
          color: lightMode ? theme.palette.common.white : theme.palette.grey[800],
          backgroundColor: lightMode ? theme.palette.grey[800] : theme.palette.common.white,
          '&:hover': {
            backgroundColor: lightMode ? theme.palette.grey[700] : theme.palette.grey[400]
          }
        }),
        // TEXT
        ...(textVariant && {
          '&:hover': {
            backgroundColor: theme.palette.grey['500Transparent'][24]
          }
        }),
        // SOFT
        ...(softVariant && {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.grey['500Transparent'][8],
          '&:hover': {
            backgroundColor: theme.palette.grey['500Transparent'][24]
          }
        }),
        // WHITE
        ...(whiteVariant && {
          color: theme.palette.grey[800],
          backgroundColor: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.grey[200]
          }
        }),
        // OUTLINED - handling only inherit color styles
        ...(outlinedVariant && {
          borderColor: inheritColor ? theme.palette.grey['500Transparent'][32] : 'currentColor',
          '&:hover': {
            borderColor: 'currentColor',
            boxShadow: '0 0 0 0.5px currentColor',
            backgroundColor: alpha(theme.palette.action.hover, 0.08)
          }
        })
      })
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        // OUTLINED - handling all other color styles
        ...(outlinedVariant && {
          borderColor: inheritColor ? theme.palette.grey['500Transparent'][32] : 'currentColor',
          '&:hover': {
            borderColor: inheritColor ? theme.palette.grey[900] : 'currentColor',
            boxShadow: '0 0 0 0.5px currentColor',
            backgroundColor: inheritColor
              ? alpha(theme.palette.action.hover, 0.08)
              : alpha(theme.palette[ownerState.color].main, 0.08)
          }
        }),
        // CONTAINED
        ...(containedVariant && {
          backgroundColor: theme.palette.primary.main,
          '&:hover': {
            boxShadow: theme.customShadows[color]
          }
        }),

        // TEXT
        ...(textVariant && {
          '&:hover': {
            backgroundColor: alpha(theme.palette[ownerState.color].main, 0.08)
          }
        }),
        // SOFT
        ...(softVariant && {
          color: theme.palette[color][lightMode ? 'darker' : 'lighter'],
          backgroundColor: alpha(theme.palette[color].main, 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.16)
          }
        }),
        // WHITE
        ...(whiteVariant && {
          color: theme.palette[color].main,
          backgroundColor: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.grey[200]
          }
        })
      })
    }));

    const disabledState = {
      [`&.${buttonClasses.disabled}`]: {
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette.action.disabledBackground
        })
      }
    };

    const size = {
      ...(smallSize && {
        height: 34,
        fontSize: 13,
        paddingLeft: 8,
        paddingRight: 8,
        borderRadius: 6,
        ...(textVariant && {
          paddingLeft: 4,
          paddingRight: 4
        })
      }),
      ...(mediumSize && {
        height: 36,
        fontSize: 14,
        paddingLeft: 12,
        paddingRight: 12,
        borderRadius: 8,
        ...(textVariant && {
          paddingLeft: 8,
          paddingRight: 8
        })
      }),
      ...(largeSize && {
        height: 48,
        fontSize: 15,
        paddingLeft: 16,
        paddingRight: 16,
        borderRadius: 10,
        ...(textVariant && {
          paddingLeft: 10,
          paddingRight: 10
        })
      }),
      ...(xlSize && {
        height: 56,
        paddingLeft: 24,
        paddingRight: 24,
        borderRadius: 16
      })
    };

    return [defaultStyle, ...colorStyle, disabledState, size];
  };

  return {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonProps }) => rootStyles(ownerState)
      }
    }
  };
}
